import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { getStuckStamps } from '../services/stamp-service';

// Approved stamps only auto-sign on the customer dashboard ('/') — its
// Realtime subscription and stuck-stamp sweep live there. Anywhere else in
// the app an approval lands silently (task #15; a stamp sat unsigned 1.5h on
// cutover night). This banner watches from every OTHER route and taps through
// to '/' where the existing machinery signs it. Checks on route change,
// tab-focus, and a slow poll — deliberately no second Realtime subscription
// (the dashboard already owns the customer-stamps channel).
const POLL_MS = 60_000;

export function StampClaimBanner() {
  const { userAddress } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [waiting, setWaiting] = useState(0);

  const check = useCallback(async () => {
    if (!userAddress) return;
    try {
      const stuck = await getStuckStamps(userAddress);
      // 'submitting' means a sign is already in flight somewhere — don't nag.
      setWaiting(stuck.filter((s) => s.onchain_status !== 'submitting').length);
    } catch {
      /* non-fatal — banner just doesn't show */
    }
  }, [userAddress]);

  useEffect(() => {
    if (!userAddress || location.pathname === '/') {
      setWaiting(0);
      return;
    }
    check();
    const onVisible = () => {
      if (!document.hidden) check();
    };
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(check, POLL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
    };
  }, [userAddress, location.pathname, check]);

  if (!waiting || location.pathname === '/') return null;

  // Sits ABOVE the tab bar, never on top of it. At bottom-4 this banner covered
  // the whole TabBar, which in owner mode is the only route to COUNTER / STATS
  // / SETUP / MORE — so an owner holding an unclaimed stamp lost all access to
  // their settings, with nothing on screen to explain why.
  // Offset = nav (12 pt + 44 button + 18 pb) + coral cap (14) + a 16 gap, plus
  // the home-indicator inset that the cap itself absorbs.
  return (
    <button
      onClick={() => navigate('/')}
      className="fixed left-4 right-4 z-50 mx-auto max-w-md bg-white rounded-[16px] p-4 text-left shadow-pixel bottom-[calc(6.5rem+env(safe-area-inset-bottom))]"
    >
      <p className="font-heading text-[9px] text-gold-ink tracking-[1px]">⭐ STAMP READY TO CLAIM</p>
      <p className="mt-1 font-grotesk font-medium text-[12px] text-body-grey">
        {waiting === 1 ? 'A stamp was approved' : `${waiting} stamps were approved`} — tap to
        collect your beans.
      </p>
    </button>
  );
}
